import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { PublicLayout } from './BookPage';
import { useI18n } from '../i18n/I18nContext';
import { SupportButton } from '../components/SupportButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ShieldCheck, Heart, Globe, Mail, Star } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function AboutPage() {
  const [form, setForm] = useState({ name: '', email: '', organisation: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useI18n();

  const apply = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/ambassadors/apply', form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40 font-bold';

  return (
    <PublicLayout testid="about-page" pageKey="page_apropos">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring} className="max-w-3xl">
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('about.eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3">
          {t('about.title')}
        </h1>
        <p className="text-base sm:text-lg text-brume leading-relaxed mt-6">
          {t('about.intro')}
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-6 mt-12">
        {[
          { icon: Heart, t: t('about.v1t'), d: t('about.v1d') },
          { icon: ShieldCheck, t: t('about.v2t'), d: t('about.v2d') },
          { icon: Globe, t: t('about.v3t'), d: t('about.v3d') },
        ].map((f, i) => (
          <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.1 + i * 0.08 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8">
            <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center mb-5">
              <f.icon className="w-6 h-6 text-or-400" strokeWidth={2.5} />
            </span>
            <h3 className="text-xl font-semibold text-creme mb-2">{f.t}</h3>
            <p className="text-base text-brume leading-relaxed">{f.d}</p>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.35 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 mt-12" data-testid="ambassador-section">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center">
            <Star className="w-6 h-6 text-or-400" strokeWidth={2.5} />
          </span>
          <h2 className="text-2xl font-semibold text-creme">{t('about.teamTitle')}</h2>
        </div>
        <p className="text-base text-brume leading-relaxed max-w-2xl">
          {t('about.teamText')}
        </p>
        {done ? (
          <div className="mt-6 bg-or-400/10 border border-or-400/40 rounded-2xl p-6 text-center" data-testid="ambassador-success">
            <p className="font-bold text-or-300 text-lg">{t('about.ambSuccessTitle')}</p>
            <p className="text-sm text-brume mt-2">{t('about.ambSuccessText', { email: form.email })}</p>
          </div>
        ) : (
          <form onSubmit={apply} className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="ambassador-form">
            <Input data-testid="ambassador-name-input" required value={form.name} placeholder={t('about.ambName')}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <Input data-testid="ambassador-email-input" required type="email" value={form.email} placeholder={t('about.ambEmail')}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <Input data-testid="ambassador-org-input" value={form.organisation} placeholder={t('about.ambOrg')}
              onChange={(e) => setForm({ ...form, organisation: e.target.value })} className={`${inputCls} sm:col-span-2`} />
            <Textarea data-testid="ambassador-message-input" rows={3} value={form.message}
              placeholder={t('about.ambMessage')}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="sm:col-span-2 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
            <Button type="submit" data-testid="ambassador-submit" disabled={loading}
              className="sm:col-span-2 h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
              {loading ? t('common.sending') : t('about.ambSubmit')}
            </Button>
          </form>
        )}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.38 }}
        className="bg-nuit-800 border border-or-400/30 rounded-3xl p-8 sm:p-10 mt-12 text-center gold-glow"
        data-testid="support-section">
        <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-6 h-6 text-or-400" strokeWidth={2.5} />
        </span>
        <h2 className="text-2xl font-semibold text-creme">{t('about.supportTitle')}</h2>
        <p className="text-base text-brume mt-3 max-w-xl mx-auto">
          {t('about.supportText')}
        </p>
        <div className="mt-6">
          <SupportButton />
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.4 }}
        className="bg-nuit-800 border border-or-400/30 rounded-3xl p-8 sm:p-10 mt-12 text-center gold-glow">
        <Mail className="w-8 h-8 text-or-400 mx-auto mb-4" strokeWidth={2.5} />
        <h2 className="text-2xl font-semibold text-creme">{t('about.clubTitle')}</h2>
        <p className="text-base text-brume mt-3 max-w-xl mx-auto">
          {t('about.clubText')}
        </p>
        <Link to="/demande-acces" data-testid="about-cta">
          <Button className="mt-6 h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8">
            {t('about.clubCta')}
          </Button>
        </Link>
      </motion.div>
    </PublicLayout>
  );
}
